import type { StorageAttachmentRecord } from 'hb-react/shared/hooks/useGlobalStorageV2/types';

// Common interfaces used by both formats
interface NewsCategory {
  id: number;
  name: string;
}

interface NewsLikes {
  likeableId: number;
  count: number;
}

interface NewsUpload {
  id: number;
  fileName: string;
  fileUrl: string;
}

interface NewsAttachedImage {
  id: number;
  name: string;
  defaultUrl: string;
}

interface NewsGroup {
  id: number;
  name: string;
  coverPictureSmallUrl: string;
}

// Legacy cover picture structure (used in old format)
interface NewsLegacyCoverPicture {
  originalUrl: string;
  compressedUrl: string;
  downloadUrl: string;
  thumbnailUrl: string;
  previewUrl: string;
  iconUrl: string;
  friendUrl: string;
  miniUrl: string;
  mediumUrl: string;
  wideUrl: string;
  htmlUrl: string;
  id: number;
  byteSize: number;
  filename: string;
  mimeType: string;
  virusScanStatus: string;
  fileUrl: string;
  size: number;
  name: string;
  fileName: string;
}

// New cover picture structure (used in new format)
interface NewsCoverPicture {
  compressedUrl: string;
  previewUrl: string;
  mediumUrl: string;
  wideUrl: string;
}

// Related letters structure
export interface NewsRelatedLetter {
  id: number;
  subject: string;
  content: string;
  pinned: boolean;
  publishedAt: string;
  feedableAt: string;
  categories: NewsCategory[];
  newCoverPicture: NewsLegacyCoverPicture;
}

export interface GetNewsByIdResponse {
  attachedImages: NewsAttachedImage[];
  attachments: StorageAttachmentRecord[];
  canonicalUrl: string;
  categories: NewsCategory[];
  content: string;
  createdAt: string;
  externalUrl: string | null;
  feedableAt: string;
  id: number;
  likes: NewsLikes;
  newCoverPicture: NewsLegacyCoverPicture;
  ogUrl: string;
  parentId: number;
  parentType: string;
  pinned: boolean;
  publishedAt: string;
  relatedLetters: NewsRelatedLetter[];
  seoDescription: string;
  seoImage: string;
  seoTitle: string;
  socialMediaList: Array<string>;
  subject: string;
  type: string;
  updatedAt: string;
  uploads: NewsUpload[];
}
export interface GetNewsByUuidResponse {
  id: number;
  uuid: string;
  groupIsCreatorId: number | null;
  pinnedToFeed: boolean;
  pinnedToIndexPage: boolean;
  public: boolean;
  publicationTimezone: string | null;
  publishedAt: string;
  rssUrl: string | null;
  seoCanonicalUrl: string;
  seoDescription: string;
  seoTitle: string;
  socialMediaList: Array<string>;
  visibleOnFeed: boolean;
  visibleOnIndexPage: boolean;
  type: string;
  content: string;
  sanitizedContent: string;
  subject: string;
  emailContent: string;
  coverPicture: NewsCoverPicture;
  attachments: StorageAttachmentRecord[];
  ogUrl: string;
  groups: NewsGroup[];
  categories: NewsCategory[];
  likes: NewsLikes;
}

export interface PublishedNewsData {
  id: number;
  uuid: string;
  publishedAt: string;
  seoCanonicalUrl: string;
  seoDescription: string;
  seoTitle: string;
  socialMediaList: Array<string>;
  content: string;
  subject: string;
  coverPicture: NewsCoverPicture;
  attachments: StorageAttachmentRecord[];
  ogUrl: string;
  categories: NewsCategory[];
  likes: NewsLikes;
  relatedLetters: NewsRelatedLetter[];

  /** @deprecated remove once flagEvNewsRevamp is removed */
  externalUrl?: GetNewsByIdResponse['externalUrl'];
}

export interface WrappedGetNewsByIdResponse {
  postCommunication: GetNewsByIdResponse;
}

// Union type for API responses
export type GetNewsResponse =
  | GetNewsByUuidResponse // Direct new format (by UUID)
  | WrappedGetNewsByIdResponse; // Wrapped old format (by ID)

// Interface for the related news API response structure (camelCase after fetch transformation)
export interface RelatedNewsItem {
  uuid: string;
  publishedAt: string;
  seoDescription: string | null;
  seoTitle: string | null;
  content: string;
  subject: string;
  coverPicture: {
    compressedUrl: string;
    previewUrl: string;
    mediumUrl: string;
    wideUrl: string;
  };
}

// Normalized interface for what NewsRelatedRow component expects
export interface RelatedNewsNormalized {
  content: string;
  coverPicture?: {
    previewUrl?: string;
    mediumUrl?: string;
  };
  seoTitle?: string;
  id?: number;
  uuid?: string;
  seoDescription?: string;
  publishedAt: string;
  subject: string;
}

// Type guards to distinguish between formats
export const isWrappedGetNewsByIdResponse = (
  news: GetNewsResponse,
): news is WrappedGetNewsByIdResponse => 'postCommunication' in news;

export const isGetNewsByUuidResponse = (
  news: GetNewsResponse,
): news is GetNewsByUuidResponse => 'uuid' in news;
